import React from 'react';
import logger from 'utils/logger';
import { useNavigate } from 'react-router-dom';
import { PERMISSION_ERROR_CODES, AUTH_ERROR_CODES } from '../constants/errorCodes';
import { auth } from '../firebase';

class FirebaseErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    logger.error('FirebaseErrorBoundary caught an error:', error, errorInfo);
    this.setState({ errorInfo });

    // Don't redirect if the error has been marked as handled (e.g., by a try-catch in a component)
    if (error.handled || error.skipRedirect) {
      logger.debug('FirebaseErrorBoundary - Error already handled, not redirecting:', {
        code: error.code,
        message: error.message
      });
      return;
    }

    // Check if this is a Firebase permission error
    if (error.code && PERMISSION_ERROR_CODES.includes(error.code)) {
      // Only redirect if this is an unhandled error during render
      // Permission errors in async operations (like reactivation) should be handled locally
      logger.warn('FirebaseErrorBoundary - Unhandled permission error, redirecting to unauthorized');
      if (this.props.navigate) {
        this.props.navigate('/unauthorized');
      } else {
        window.location.href = '/unauthorized';
      }
      return;
    }

    // Check if this is an authentication error
    if (error.code && AUTH_ERROR_CODES.includes(error.code)) {
      // Only redirect on actual auth errors (user not authenticated)
      // Don't redirect on permission errors for specific operations
      logger.warn('FirebaseErrorBoundary - Authentication error, redirecting to login');
      if (this.props.navigate) {
        this.props.navigate('/login');
      } else {
        window.location.href = '/login';
      }
      return;
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // If error state changed and it's an auth error, redirect immediately
    if (this.state.hasError && !prevState.hasError && this.state.error) {
      const error = this.state.error;

      // Check if user is actually logged out
      const isLoggedOut = !auth.currentUser;

      // Check if this is an authentication error
      const isAuthError = error.code && AUTH_ERROR_CODES.includes(error.code);

      // Also check error message for common auth-related errors
      const errorMessage = error.message || error.toString() || '';
      const hasAuthErrorInMessage = errorMessage.toLowerCase().includes('unauthenticated') ||
      errorMessage.toLowerCase().includes('auth') ||
      errorMessage.toLowerCase().includes('permission-denied');

      if ((isAuthError || (isLoggedOut && hasAuthErrorInMessage)) && !error.handled && !error.skipRedirect) {
        logger.warn('FirebaseErrorBoundary - Detected auth error, redirecting to login');
        // Use replace to prevent back button issues
        if (this.props.navigate) {
          this.props.navigate('/login', { replace: true });
        } else {
          window.location.replace('/login');
        }
      }
    }
  }

  handleReset = () => {
    // Check if user is logged out before resetting
    if (!auth.currentUser) {
      logger.warn('FirebaseErrorBoundary - User logged out, redirecting to login instead of resetting');
      if (this.props.navigate) {
        this.props.navigate('/login', { replace: true });
      } else {
        window.location.replace('/login');
      }
      return;
    }
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleRefresh = () => {
    // Check if this is an auth error or user is logged out
    const error = this.state.error;
    const isLoggedOut = !auth.currentUser;
    const isAuthError = error?.code && AUTH_ERROR_CODES.includes(error.code);

    if (isAuthError || isLoggedOut) {
      logger.warn('FirebaseErrorBoundary - Auth error detected, redirecting to login instead of reloading');
      if (this.props.navigate) {
        this.props.navigate('/login', { replace: true });
      } else {
        window.location.replace('/login');
      }
    } else {
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      const error = this.state.error;
      const isLoggedOut = !auth.currentUser;
      const isAuthError = error?.code && AUTH_ERROR_CODES.includes(error.code);

      // If it's an auth error or user is logged out, show a message and redirect will happen in componentDidUpdate
      if ((isAuthError || isLoggedOut) && !error?.handled && !error?.skipRedirect) {
        // Show a brief message while redirecting
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-900">Redirecting to login...</h2>
              <p className="text-sm text-gray-600 mt-2">Please wait</p>
            </div>
          </div>);

      }

      // You can render any custom fallback UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md w-full space-y-8">
            <div className="text-center">
              <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Something went wrong</h2>
              <p className="mt-2 text-sm text-gray-600">
                An unexpected error occurred. You can try to recover or refresh the page.
              </p>
              {process.env.NODE_ENV === 'development' && this.state.error &&
              <details className="mt-3 text-start" style={{ maxWidth: '600px', margin: '20px auto' }}>
                <summary className="text-muted small">Error details (dev only)</summary>
                <pre className="small mt-2 text-muted" style={{ overflow: 'auto', textAlign: 'left' }}>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
              }
              <div className="mt-4 flex gap-3 justify-center">
                <button
                  onClick={this.handleReset}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500">

                  Try Again
                </button>
                <button
                  onClick={this.handleRefresh}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500">

                  Refresh Page
                </button>
              </div>
            </div>
          </div>
        </div>);

    }

    return this.props.children;
  }
}

// Wrapper component that provides navigation
const FirebaseErrorBoundaryWithNavigation = ({ children }) => {
  const navigate = useNavigate();

  return <FirebaseErrorBoundary navigate={navigate}>{children}</FirebaseErrorBoundary>;
};

export default FirebaseErrorBoundaryWithNavigation;
